import { AxiosResponse } from "axios";
import cn from "classnames";
import React, { ReactNode } from "react";

import ButtonBase from "shared/ui/controls/ButtonBase";
import { getContentDispositionFileName } from "widgets/ImportDocumentation";

import { getArrayFilesInBase64 } from "utils/formatters/getArrayFilesInBase64";
import { errorCatcher } from "utils/helpers/errorCatcher";

import { ReactComponent as WarningIcon } from "shared/assets/icons/Icon Warning.svg";
import { ReactComponent as ExcelIcon } from "shared/assets/icons/Icon-ChooseImportFile.svg";
import { ReactComponent as SuccessIcon } from "shared/assets/icons/Icon-success.svg";

import styles from "./ImportModal.module.scss";

export interface ImportModalProps {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setCountOfPositions: React.Dispatch<React.SetStateAction<number>>;
  setErrList: React.Dispatch<React.SetStateAction<object>>;
  setIsErr: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: () => void;
  isErr: boolean;
  errList: any;
  countOfPositions: number;
  isSuccess: boolean;
  renderDownloadBtn?: (onDownload: () => void) => ReactNode;
  formatSampleFileName?: (response: AxiosResponse) => string;
  downloadSampleRequest: () => Promise<AxiosResponse>;
  uploadDocumentationRequest: ({
    name,
    file,
  }: {
    name: string;
    file: string | ArrayBuffer | null;
  }) => Promise<AxiosResponse>;
}

const ImportModal: React.FC<ImportModalProps> = ({
  setIsLoading,
  setIsModalOpen,
  setIsSuccess,
  setCountOfPositions,
  setErrList,
  setIsErr,
  isErr,
  errList,
  isSuccess,
  countOfPositions,
  refresh,
  renderDownloadBtn,
  downloadSampleRequest,
  uploadDocumentationRequest,
  formatSampleFileName,
}) => {
  const handleDownloadSample = () => {
    downloadSampleRequest()
      .then((response) => {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(new Blob([response.data], { type: response.headers.contentType }));
        link.download = formatSampleFileName?.(response) || getContentDispositionFileName(response);
        link.click();
      })
      .catch(errorCatcher);
  };

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setIsErr(false);
    const file: any = event.target.files?.item(0);
    getArrayFilesInBase64([file]).then((arrayFilesInBase64) => {
      uploadDocumentationRequest({
        name: arrayFilesInBase64[0].name,
        file: arrayFilesInBase64[0].file,
      })
        .then((res) => {
          setCountOfPositions(res.data.created);
          refresh();
          setIsLoading(false);
          setIsSuccess(true);
        })
        .catch((err) => {
          setIsLoading(false);
          if (!err.response.data.detail) {
            setErrList(err.response.data?.errors);
            setIsErr(true);
          }
          errorCatcher(err);
        });
    });
  };

  const handleSuccess = () => {
    setIsModalOpen(false);
    setIsSuccess(false);
    setCountOfPositions(0);
    setIsErr(false);
    setErrList({});
  };

  return !isSuccess ? (
    <>
      {!isErr ? (
        <>
          <ExcelIcon className={styles.icon} />
          <p className={styles.modaltext}>
            Импорт происходит согласно шаблону.
            <br />
            Заполните шаблон и импортируйте файл xlsx
          </p>
        </>
      ) : (
        <>
          <WarningIcon className={styles.icon} />
          <p className={styles.modaltext}>Позиции не сохранены. Требуются исправления:</p>
          <div className={styles.titles}>
            <h4>Строка</h4>
            <h4>Ошибка</h4>
          </div>
          <div className={styles.listbox}>
            {errList &&
              Object.keys(errList).map((el) => {
                return (
                  <div className={styles.table} key={el}>
                    <div className={styles.string}>
                      <h4>{el}</h4>
                    </div>
                    <div className={styles.mistake}>{errList[el]}</div>
                  </div>
                );
              })}
          </div>
        </>
      )}
      <div className={styles.buttons}>
        {renderDownloadBtn ? (
          renderDownloadBtn(handleDownloadSample)
        ) : (
          <ButtonBase secondary onClick={handleDownloadSample}>
            Скачать шаблон
          </ButtonBase>
        )}
        <label>
          <div className={styles.btn}>Выбрать файл</div>
          <input type="file" className={styles.input} onChange={handleChangeInput} />
        </label>
      </div>
    </>
  ) : (
    <>
      <div>
        <SuccessIcon className={cn(styles.icon, styles.success)} />
        <p className={styles.modaltext}>В систему успешно добавлены позиции: {countOfPositions}</p>
      </div>
      <div className={styles.buttons} style={{ justifyContent: "center" }}>
        <ButtonBase primary onClick={handleSuccess}>
          Хорошо!
        </ButtonBase>
      </div>
    </>
  );
};

export default React.memo(ImportModal);
